/**
 * Ticket Lifecycle v2 — Slice 4E
 *
 * React Query hook that fetches the three data slices needed by the
 * pure `computeAdvisorMetrics` helper:
 *
 *   1. advisor_action_events filtered by advisor_user_id + shop_id +
 *      action_kind in {ticket_accepted, kicked_back_to_tech} +
 *      recent 30 days.
 *   2. ticket_custody_events filtered by shop_id + the ticket_ids
 *      from #1 + recent 30 days (timing pairs only).
 *   3. ticket_custody current-state row counts for the shop
 *      (pending_advisor_accept, with_advisor).
 *
 * Gated by the `advisorTimingAnalytics` feature flag (default OFF in
 * every preset; opt-in via /feature-flags). When the flag is off, the
 * query is `enabled: false` — zero network, zero cost.
 *
 * Boundary: this module reads from custody/advisor tables only. It
 * does not import scheduling, timer, parking, rollover, bay, or
 * invariants code.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import {
  computeAdvisorMetrics,
  ADVISOR_METRICS_WINDOW_DAYS,
  type AdvisorMetrics,
  type AdvisorActionEventLite,
  type CustodyEventLite,
} from '@/lib/tickets/lifecycle/advisorMetrics';

interface UseAdvisorMetricsArgs {
  shopId: string | null | undefined;
  advisorUserId: string | null | undefined;
}

const WINDOW_MS = ADVISOR_METRICS_WINDOW_DAYS * 24 * 60 * 60 * 1000;

export function useAdvisorMetrics({
  shopId,
  advisorUserId,
}: UseAdvisorMetricsArgs) {
  const advisorTimingAnalyticsEnabled = useFeatureFlag('advisorTimingAnalytics');
  const enabled =
    advisorTimingAnalyticsEnabled && !!shopId && !!advisorUserId;

  // Compute the window cutoff once per render. `useMemo` avoids
  // re-creating it on every re-render of the consumer.
  const windowStart = useMemo(
    () => new Date(Date.now() - WINDOW_MS).toISOString(),
    // Recomputed only when the consumer remounts; deliberate so the
    // query cache key is stable for the duration of a session.
    [],
  );

  return useQuery({
    queryKey: ['advisor-metrics', shopId, advisorUserId, windowStart],
    enabled,
    retry: false,
    queryFn: async (): Promise<AdvisorMetrics> => {
      // 1. Advisor action events (counts).
      const { data: actionRows, error: actionErr } = await (supabase as any)
        .from('advisor_action_events')
        .select('action_kind, ticket_id, created_at')
        .eq('shop_id', shopId)
        .eq('advisor_user_id', advisorUserId)
        .in('action_kind', ['ticket_accepted', 'kicked_back_to_tech'])
        .gte('created_at', windowStart);
      if (actionErr) {
        console.error('[advisor-metrics] action events query failed', actionErr);
        throw actionErr;
      }
      const advisorActionEvents: AdvisorActionEventLite[] =
        (actionRows ?? []).map((r: { action_kind: string; ticket_id: string; created_at: string }) => ({
          action_kind: r.action_kind as AdvisorActionEventLite['action_kind'],
          ticket_id: r.ticket_id,
          created_at: r.created_at,
        }));

      // 2. Ticket custody events for the same advisor's tickets in the
      //    window. We fetch by shop+window and let the pairing helper
      //    filter by actor_id — broader fetch, cheaper join.
      const { data: custodyRows, error: custodyErr } = await (supabase as any)
        .from('ticket_custody_events')
        .select('event_kind, ticket_id, actor_id, created_at')
        .eq('shop_id', shopId)
        // Slice 5A.3 widens this to include `boh_accept` so the
        // advisor-lifecycle-cycle metric (kick_to_foh → boh_accept) can
        // be paired by the pure helper. The four kinds together form a
        // single complete advisor handoff cycle.
        .in('event_kind', ['kick_to_foh', 'foh_accept', 'kick_to_boh', 'boh_accept'])
        .gte('created_at', windowStart);
      if (custodyErr) {
        console.error('[advisor-metrics] custody events query failed', custodyErr);
        throw custodyErr;
      }
      const custodyEvents: CustodyEventLite[] =
        (custodyRows ?? []).map((r: { event_kind: string; ticket_id: string; actor_id: string | null; created_at: string }) => ({
          event_kind: r.event_kind as CustodyEventLite['event_kind'],
          ticket_id: r.ticket_id,
          actor_id: r.actor_id,
          created_at: r.created_at,
        }));

      // 3. Current snapshot counts. Shop-wide; not advisor-scoped (no
      //    advisor owns a pending_advisor_accept ticket).
      const { data: currentRows, error: currentErr } = await (supabase as any)
        .from('ticket_custody')
        .select('custody_status')
        .eq('shop_id', shopId)
        .in('custody_status', ['pending_advisor_accept', 'with_advisor']);
      if (currentErr) {
        console.error('[advisor-metrics] current counts query failed', currentErr);
        throw currentErr;
      }
      let pendingAdvisorAccept = 0;
      let withAdvisor = 0;
      for (const row of (currentRows ?? []) as Array<{ custody_status: string }>) {
        if (row.custody_status === 'pending_advisor_accept') pendingAdvisorAccept += 1;
        else if (row.custody_status === 'with_advisor') withAdvisor += 1;
      }

      return computeAdvisorMetrics({
        advisorActionEvents,
        custodyEvents,
        advisorUserId: advisorUserId as string,
        currentCounts: { pendingAdvisorAccept, withAdvisor },
      });
    },
  });
}
